import React from 'react';

import { faEdit, faPlus, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    createMembershipGroupCardFormSchema,
    type CreateMembershipGroupCardFormSchemaValues,
    useCreateMembershipGroupCard,
} from 'src/features/memberships/api/useCreateMembershipGroupCard';
import { useUpdateMembershipGroupCard } from 'src/features/memberships/api/useUpdateMembershipGroupCard';
import { MembershipGroupCardForm } from 'src/features/memberships/components/forms/MembershipGroupCardForm';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    DialogHeader,
    DialogSeparator,
    DialogBody,
    DialogFooter,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { MembershipGroup, MembershipGroupCard } from 'src/features/memberships/types';

interface MembesrhipGroupCardEditFormModalProps {
    onSuccess: (values: CreateMembershipGroupCardFormSchemaValues) => void;
    parkingId: string;
    membershipGroup: MembershipGroup;
    membershipGroupCard: MembershipGroupCard | null;
    isCardModalOpen: boolean;
    setIsCardModalOpen: (isOpen: boolean) => void;
    modalMode?: 'edit' | 'add';
}
const MembershipGroupCardFormModal = ({
    onSuccess,
    parkingId,
    membershipGroup,
    membershipGroupCard,
    isCardModalOpen,
    setIsCardModalOpen,
    modalMode,
}: MembesrhipGroupCardEditFormModalProps) => {
    const { formatMessage } = useIntl();

    const form = useForm<CreateMembershipGroupCardFormSchemaValues>({
        resolver: zodResolver(createMembershipGroupCardFormSchema),
        defaultValues: {
            number: membershipGroupCard?.number ?? '',
            tags: membershipGroupCard?.tags.map((tag) => tag.card_tag.id) ?? [],
            description: membershipGroupCard?.description ?? undefined,
        },
    });

    React.useEffect(() => {
        if (membershipGroupCard) {
            form.reset({
                number: membershipGroupCard?.number,
                tags: membershipGroupCard?.tags.map((tag) => tag.card_tag.id),
                description: membershipGroupCard?.description ?? undefined,
            });
        }
    }, [membershipGroupCard]);

    const { mutate: createMembershipGroupCard, isPending: isPendingCreate } = useCreateMembershipGroupCard({
        onSuccess: () => {
            if (onSuccess) {
                onSuccess(form.getValues());
            }
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.createMembershipGroupCardSuccess),
                description: formatMessage(membershipMessages.createMembershipGroupCardSuccessDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);

            const errorMessage = () => {
                if (errorData?.code === 'membership_card_exceeding_due_amount') {
                    return formatMessage(membershipMessages.membershipCardExceedingDueAmount);
                }
                if (errorData?.code === 'membership_card_exceeding_due_amount_guest') {
                    return formatMessage(membershipMessages.membershipCardExceedingDueAmountGuest);
                }
                if (errorData?.card_number) {
                    return formatMessage(membershipMessages.cardNumberExists);
                }
                if (errorData?.all[0].message) {
                    return errorData?.all[0].message;
                }
                return formatMessage(generalErrorMessages.unknownError);
            };

            toast({
                title: errorCode,
                description: errorMessage(),
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateMembershipGroupCard, isPending: isPendingUpdate } = useUpdateMembershipGroupCard({
        onSuccess: () => {
            if (onSuccess) {
                onSuccess(form.getValues());
            }
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.editMembershipGroupCardSuccess),
                description: formatMessage(membershipMessages.editMembershipGroupCardSuccessDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleSubmit = (values: CreateMembershipGroupCardFormSchemaValues) => {
        const submitData = {
            membershipGroupId: membershipGroup.id,
            parkingId,
            ...values,
        };

        if (membershipGroupCard?.id) {
            updateMembershipGroupCard({ membershipGroupCardId: membershipGroupCard.id, ...submitData });
        } else {
            createMembershipGroupCard(submitData);
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset({
                number: '',
                tags: [],
                description: undefined,
            });
        }
        if (setIsCardModalOpen) setIsCardModalOpen(isOpen);
    };

    const modalTitle = modalMode === 'add' ? membershipMessages.addVehicle : membershipMessages.editVehicle;
    const modalIcon = modalMode === 'add' ? faPlus : faEdit;
    const modalDescription =
        modalMode === 'add'
            ? formatMessage(membershipMessages.addVehicleDescription, { membershipName: membershipGroup.name })
            : formatMessage(membershipMessages.editVehicleDescription, { vehicleNumber: membershipGroupCard?.number });
    return (
        <Dialog open={isCardModalOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={modalIcon} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...modalTitle} />
                            </DialogTitle>
                            <DialogDescription>{modalDescription}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <MembershipGroupCardForm
                        form={form}
                        onSubmit={handleSubmit}
                        parkingId={parkingId}
                        membershipGroupId={membershipGroup.id}
                    />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPendingUpdate || isPendingCreate}
                        type="submit"
                        onClick={form.handleSubmit(handleSubmit)}
                        className="flex-1"
                    >
                        {isPendingUpdate || isPendingCreate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...generalMessages.saveChanges} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { MembershipGroupCardFormModal };
